/**
 * Forgot password (tracker item 45): company + email, and one neutral answer
 * whether or not an account exists. Staff do not use this (their passwords are
 * reset by an administrator); the answer is the same for them too.
 */
import React, { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { createAPIClient, IPortalApi } from '../../api';
import { PortalPublicLayout, portalLinkClass } from './PortalPublicLayout';
import { InlineAlert } from './PortalStates';
import { inputClass, labelClass, primaryButtonClass } from '../../components/formStyles';
import { describePortalFailure, slugFromParam } from '../../utils/portal';

export function PortalForgotPasswordPage() {
  const api = useMemo(() => createAPIClient() as IPortalApi, []);
  const { slug: slugParam } = useParams<{ slug: string }>();
  const [search] = useSearchParams();
  const fixedSlug = slugFromParam(slugParam) || slugFromParam(search.get('company') ?? undefined);
  const [tenantSlug, setTenantSlug] = useState(fixedSlug);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busy) return;
    setError(null);
    if (!/^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/.test(tenantSlug.trim())) return setError('Enter your company name as it appears in your invoice link.');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return setError('Enter a valid email address.');
    setBusy(true);
    try {
      await api.requestPortalPasswordReset({ tenantSlug: tenantSlug.trim().toLowerCase(), email: email.trim() });
      setSent(true);
    } catch (cause) {
      setError(describePortalFailure(cause, 'We could not send the request. Please try again.').message);
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <PortalPublicLayout title="Check your email">
        <p role="status" className="text-sm text-gray-700" data-testid="reset-requested">
          If there is an online account for this email address, we have sent a message to it with a link to choose a new password. The link works once and expires in one hour.
        </p>
        <Link to="/portal/login" className={`${portalLinkClass} mt-3`}>Back to sign in</Link>
      </PortalPublicLayout>
    );
  }

  const input = inputClass + ' min-h-[44px] text-base';
  return (
    <PortalPublicLayout title="Forgot your password?" intro="Enter your email address and we will send you a link to choose a new password.">
      <form onSubmit={(e) => void submit(e)} className="space-y-4" noValidate>
        {!fixedSlug && (
          <div>
            <label htmlFor="forgot-company" className={labelClass}>Company</label>
            <input id="forgot-company" className={input} value={tenantSlug} onChange={(e) => setTenantSlug(e.target.value)} disabled={busy} autoCapitalize="none" required />
          </div>
        )}
        <div>
          <label htmlFor="forgot-email" className={labelClass}>Email address</label>
          <input id="forgot-email" type="email" className={input} value={email} onChange={(e) => setEmail(e.target.value)} disabled={busy} autoComplete="email" required />
        </div>
        {error && <InlineAlert>{error}</InlineAlert>}
        <button type="submit" className={`${primaryButtonClass} w-full text-base`} disabled={busy}>{busy ? 'Sending…' : 'Send reset link'}</button>
      </form>
      <Link to="/portal/login" className={`${portalLinkClass} mt-3`}>Back to sign in</Link>
    </PortalPublicLayout>
  );
}
